import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { MOSTRAR_PLANES } from '../config/comercial';
import AvisoTimbrado from '../components/AvisoTimbrado';
import AvisoLegal from '../components/AvisoLegal';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Users, Settings, LogOut, Package, HardDrive,
  CreditCard, Tag, ShoppingCart, UserCheck, History, Truck,
  ShoppingBag, FileText, Sun, Moon, Archive, BarChart2,
  AlertTriangle, Clock, X, ClipboardList, Menu,
} from 'lucide-react';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';
import { useTheme } from '../context/ThemeContext';
import { useMeta } from '../hooks/useMeta';
import { useNombreApp } from '../hooks/useNombreApp';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function SubscriptionBanner({ userRol }) {
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Sin planes a la vista no hace falta ni preguntar por la suscripción.
    if (!MOSTRAR_PLANES) return;
    if (userRol !== 'Administrador') return;
    const token = sessionStorage.getItem('access_token');
    if (!token) return;
    axios.get(`${API}/api/v1/inventario/subscription/`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setInfo(r.data)).catch(() => {});
  }, [userRol]);

  if (!MOSTRAR_PLANES || !info || dismissed) return null;
  const { estado, dias_restantes, grace_until, plan_name } = info;

  let cfg = null;
  // `dias_restantes` puede venir nulo cuando el inquilino no tiene fecha de
  // corte. Sin la comprobación explícita, JavaScript convierte null a cero y
  // el aviso sale diciendo que vence «en null días»: un plazo que no existe,
  // anunciado como si fuera inminente.
  if (estado === 'trial' && dias_restantes != null && dias_restantes <= 7) {
    cfg = {
      bg: 'bg-alerta-50 border-alerta-200 dark:bg-alerta-950/40 dark:border-alerta-800',
      icon: <Clock size={16} className="text-alerta-500 shrink-0" />,
      text: `Tu período de prueba del plan ${plan_name} vence en ${dias_restantes} día${dias_restantes !== 1 ? 's' : ''}. ¡Contactá al administrador para activar tu plan!`,
      textCls: 'text-alerta-800 dark:text-alerta-300',
    };
  } else if (estado === 'por_vencer' && dias_restantes != null) {
    cfg = {
      bg: 'bg-brand-50 border-brand-200 dark:bg-brand-950/40 dark:border-brand-800',
      icon: <AlertTriangle size={16} className="text-brand-800 shrink-0" />,
      text: `Tu suscripción (${plan_name}) vence en ${dias_restantes} día${dias_restantes !== 1 ? 's' : ''}. Renovála para no perder el acceso.`,
      textCls: 'text-brand-800 dark:text-brand-300',
    };
  } else if (estado === 'en_gracia') {
    const graceFmt = grace_until ? new Date(grace_until).toLocaleDateString('es-PY') : '';
    cfg = {
      bg: 'bg-error-50 border-error-200 dark:bg-error-950/40 dark:border-error-800',
      icon: <AlertTriangle size={16} className="text-error-500 shrink-0" />,
      text: `Tu suscripción venció. Tenés acceso hasta el ${graceFmt} (período de gracia). Contactá al administrador para renovar.`,
      textCls: 'text-error-800 dark:text-error-300',
    };
  }

  if (!cfg) return null;

  return (
    <div className={`mx-8 mt-4 flex items-start gap-3 px-4 py-3 rounded-xl border text-sm ${cfg.bg}`}>
      {cfg.icon}
      <p className={`flex-1 font-medium ${cfg.textCls}`}>{cfg.text}</p>
      <button onClick={() => setDismissed(true)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 shrink-0">
        <X size={14} />
      </button>
    </div>
  );
}

export default function DashboardLayout({ isPublic }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [sessionExpired, setSessionExpired] = useState(false);
  const [stockCriticoCount, setStockCriticoCount] = useState(0);
  const appName = useNombreApp();

  useEffect(() => {
    if (isPublic) return;
    const token = sessionStorage.getItem('access_token');
    if (!token) return;
    axios.get(`${API}/api/v1/inventario/dashboard-stats/`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setStockCriticoCount(r.data.stock_critico_count || 0)).catch(() => {});
  }, [isPublic]);

  const loginPath = isPublic ? '/admin/login' : '/login';

  const tenantName = !isPublic ? (sessionStorage.getItem('tenant_name') || 'Mi Ferretería') : null;
  const displayName = appName;
  useMeta(isPublic
    ? { title: `${displayName} Admin`, favicon: '/favicon-admin.svg' }
    : { title: tenantName ? `${tenantName} | ${displayName}` : displayName, favicon: '/favicon-tenant.svg' }
  );

  const clearSession = () => {
    if (isPublic) {
      // Sesión superadmin: solo limpia las claves de admin para no afectar sesiones de tenant abiertas
      localStorage.removeItem('admin_access_token');
      localStorage.removeItem('admin_refresh_token');
      localStorage.removeItem('user_type');
    } else {
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('tenant_name');
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('user_type');
    }
  };

  const handleLogout = () => {
    clearSession();
    if (isPublic) {
      window.location.replace('/admin/login');
    } else {
      navigate(loginPath);
    }
  };

  // El interceptor global de 401 está registrado en index.js — no se duplica aquí.

  // Al expirar inactividad: muestra overlay 2 segundos, luego redirige
  const handleExpiry = useCallback(() => {
    setSessionExpired(true);
    setTimeout(() => {
      if (isPublic) {
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('admin_refresh_token');
        localStorage.removeItem('user_type');
      } else {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('tenant_name');
        sessionStorage.removeItem('user_type');
      }
      window.location.replace(loginPath);
    }, 2000);
  }, [loginPath, isPublic]);

  useInactivityTimeout(loginPath, handleExpiry);

  // Rol del usuario logueado (tenant)
  const currentUser = isPublic ? null : JSON.parse(sessionStorage.getItem('user') || '{}');
  const userRol = currentUser
    ? (currentUser.rol || (currentUser.is_admin ? 'Administrador' : 'Sin rol'))
    : null;

  const ALLOWED_PATHS = {
    Administrador: null, // null = acceso total
    Supervisor: ['/system', '/system/categorias', '/system/productos', '/system/proveedores', '/system/compras', '/system/clientes', '/system/carrito', '/system/historial', '/system/ajuste-inventario', '/system/caja', '/system/reportes'],
    Cajero: ['/system/clientes', '/system/carrito', '/system/historial', '/system/caja'],
  };

  const allowedPaths = userRol
    ? (ALLOWED_PATHS[userRol] ?? null)
    : null;

  // El menú sigue el flujo de trabajo, no el orden en que se fueron
  // construyendo las pantallas.
  //
  // Antes eran trece ítems planos con Punto de Venta en séptimo lugar, debajo
  // de Categorías: lo que un cajero usa cien veces por día quedaba más abajo
  // que lo que se configura una vez y no se toca más. Y sin títulos de sección
  // había que leer la lista entera para encontrar algo.
  //
  // `destacado` marca la acción principal del día. Las secciones agrupan por
  // lo que uno está haciendo —vender, manejar mercadería, analizar,
  // administrar—, que es como se busca cuando se busca.
  const SECCIONES_INQUILINO = [
    {
      titulo: null,
      items: [
        { label: 'Punto de Venta', icon: <ShoppingCart size={20} />, path: '/system/carrito', destacado: true },
        { label: 'Panel',          icon: <LayoutDashboard size={20} />, path: '/system' },
      ],
    },
    {
      titulo: 'Ventas',
      items: [
        { label: 'Historial de Ventas', icon: <History size={20} />,   path: '/system/historial' },
        { label: 'Clientes',            icon: <UserCheck size={20} />, path: '/system/clientes' },
        { label: 'Cierre de Caja',      icon: <Archive size={20} />,   path: '/system/caja' },
      ],
    },
    {
      titulo: 'Inventario',
      items: [
        { label: 'Productos',            icon: <Package size={20} />,       path: '/system/productos' },
        { label: 'Entradas de Stock',    icon: <ShoppingBag size={20} />,   path: '/system/compras' },
        { label: 'Ajuste de Inventario', icon: <ClipboardList size={20} />, path: '/system/ajuste-inventario' },
        { label: 'Proveedores',          icon: <Truck size={20} />,         path: '/system/proveedores' },
        // Última de su sección a propósito: sirve —filtra el catálogo y agrupa
        // el reporte de artículos— pero es configuración inicial, no uso
        // diario. Las categorías además se crean solas al importar.
        { label: 'Categorías',           icon: <Tag size={20} />,           path: '/system/categorias' },
      ],
    },
    {
      titulo: 'Análisis',
      items: [
        { label: 'Reportes', icon: <BarChart2 size={20} />, path: '/system/reportes' },
      ],
    },
    {
      titulo: 'Administración',
      items: [
        { label: 'Usuarios',      icon: <Users size={20} />,    path: '/system/usuarios' },
        { label: 'Registro de operaciones', icon: <History size={20} />, path: '/system/operaciones' },
        { label: 'Configuración', icon: <Settings size={20} />, path: '/system/settings' },
      ],
    },
  ];

  const SECCIONES_PLATAFORMA = [
    {
      titulo: null,
      items: [
        { label: 'Visión General', icon: <LayoutDashboard size={20} />, path: '/admin' },
      ],
    },
    {
      titulo: 'Inquilinos',
      items: [
        { label: 'Ferreterías',   icon: <HardDrive size={20} />,  path: '/admin/tenants' },
        { label: 'Suscripciones', icon: <CreditCard size={20} />, path: '/admin/planes' },
        { label: 'Cobranzas',     icon: <FileText size={20} />,   path: '/admin/pagos' },
      ],
    },
    {
      titulo: 'Plataforma',
      items: [
        { label: 'Administradores', icon: <Users size={20} />,    path: '/admin/users' },
        { label: 'Configuración',   icon: <Settings size={20} />, path: '/admin/settings' },
      ],
    },
  ];

  // El filtrado por rol se aplica sobre los ítems y después se descartan las
  // secciones que quedaron vacías: un título de sección sin nada debajo le
  // anuncia a un cajero pantallas que no puede abrir.
  const secciones = (isPublic ? SECCIONES_PLATAFORMA : SECCIONES_INQUILINO)
    .map(seccion => ({
      ...seccion,
      items: (isPublic || allowedPaths === null)
        ? seccion.items
        : seccion.items.filter(i => allowedPaths.includes(i.path)),
    }))
    .filter(seccion => seccion.items.length > 0);

  const isActive = (path) => location.pathname === path;

  // El menu lateral ocupa 256px fijos. En un telefono de 360 eso dejaba 104
  // para el contenido, asi que de `lg` para abajo se corre fuera de pantalla
  // y se abre por encima.
  const [menuAbierto, setMenuAbierto] = useState(false);

  // Navegar cierra el menu: en un telefono queda tapando lo que se acaba de
  // abrir, y el usuario tiene que cerrarlo a mano para ver adonde llego.
  const irA = (path) => { setMenuAbierto(false); navigate(path); };

  /* ── Overlay sesión expirada ── */
  const SessionExpiredOverlay = sessionExpired ? (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/80 backdrop-blur-sm">
      <div className="bg-white rounded-3xl p-10 text-center shadow-2xl max-w-sm w-full mx-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-alerta-100 rounded-full mb-5">
          <Clock size={32} className="text-alerta-500" />
        </div>
        <h2 className="text-2xl font-black text-slate-800 mb-2">Sesión Expirada</h2>
        <p className="text-slate-500 text-sm">Tu sesión cerró por inactividad. Redirigiendo al login...</p>
      </div>
    </div>
  ) : null;

  /* ── Estilos condicionales ── */
  if (isPublic) {
    // Panel admin: siempre claro (sin toggle de tema)
    return (
      <div className="flex w-full min-h-screen bg-slate-50">
        {SessionExpiredOverlay}
        <aside className="w-64 barra-sistema border-r shadow-sm min-h-screen flex flex-col">
          <div className="p-6">
            <h2 className="text-xl font-bold tracking-tight text-white">{displayName}</h2>
            <p className="text-xs mt-1 text-negro-400">Panel de Control Principal</p>
          </div>
          <nav className="flex-1 overflow-y-auto mt-2 pb-4">
            <MenuLateral
              secciones={secciones}
              activo={isActive}
              onNavegar={navigate}
            />
          </nav>
          <div className="p-4 border-t border-negro-800 py-6">
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 mx-1 rounded-xl font-medium text-error-500 hover:bg-error-50 transition-colors">
              <LogOut size={20} />Cerrar Sesión
            </button>
          </div>
        </aside>
        <main className="flex-1 overflow-x-hidden overflow-y-auto">
          <header className="barra-sistema border-b h-16 flex items-center px-4 sm:px-8">
            <span className="text-negro-300 font-medium">Bienvenido, admin</span>
          </header>
          <div className="p-4 sm:p-6 lg:p-8"><Outlet /></div>
        </main>
      </div>
    );
  }

  // Panel tenant: con toggle claro/oscuro
  return (
    <div className="flex w-full min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors duration-200">
      {SessionExpiredOverlay}
      {/* El velo que cierra el menu al tocar fuera. Solo existe en movil. */}
      {menuAbierto && (
        <button
          type="button"
          aria-label="Cerrar el menú"
          onClick={() => setMenuAbierto(false)}
          className="lg:hidden fixed inset-0 z-30 bg-slate-900/60 backdrop-blur-sm"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`w-64 shrink-0 min-h-screen flex flex-col barra-sistema border-r transition-transform duration-200
          fixed inset-y-0 left-0 z-40 lg:static lg:translate-x-0
          ${menuAbierto ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="p-6 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold tracking-tight text-white">{displayName}</h2>
            <p className="text-xs mt-1 text-negro-400">Panel Avanzado</p>
          </div>
          {/* Toggle tema */}
          <button
            onClick={toggleTheme}
            title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            className="mt-1 p-2 rounded-xl border transition-all text-negro-300 border-negro-700 hover:bg-negro-800 hover:text-white"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto mt-2 pb-4">
          <MenuLateral
            secciones={secciones}
            activo={isActive}
            onNavegar={irA}
            avisos={{ '/system/productos': stockCriticoCount }}
          />
        </nav>

        <div className="p-4 py-6 border-t border-negro-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 mx-1 rounded-xl font-medium transition-colors text-error-500 hover:bg-error-50 dark:text-error-400 dark:hover:bg-error-950/30 dark:border-transparent dark:hover:border-error-900/50"
          >
            <LogOut size={20} />Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* Contenido principal */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto">
        <header className="h-16 flex items-center justify-between gap-2 px-4 sm:px-8 bg-white border-b border-slate-200 dark:bg-slate-900 dark:border-slate-800 transition-colors duration-200">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              aria-label="Abrir el menú"
              aria-expanded={menuAbierto}
              onClick={() => setMenuAbierto(true)}
              className="lg:hidden -ml-1 p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5"
            >
              <Menu size={22} />
            </button>
            <div className="min-w-0">
              <p className="text-xs text-slate-400 dark:text-slate-500 font-medium leading-none mb-0.5">Bienvenido a</p>
              <p className="text-base sm:text-lg font-bold text-slate-800 dark:text-white leading-tight truncate">
                {sessionStorage.getItem('tenant_name') || 'Mi Negocio'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <span className="text-xs px-2.5 py-1 rounded-full font-semibold bg-brand-50 text-brand-800 border border-brand-200 dark:bg-brand-500/10 dark:text-brand-400 dark:border-brand-500/20">
              {userRol}
            </span>
            {/* El nombre se oculta en pantallas chicas: el rol alcanza para
                saber con que permisos se esta trabajando, y el nombre empuja
                el nombre del negocio fuera de la pantalla. */}
            <span className="hidden sm:inline text-sm text-slate-600 dark:text-slate-400 font-medium">
              {currentUser?.full_name || currentUser?.username || ''}
            </span>
          </div>
        </header>
        <SubscriptionBanner userRol={userRol} />
        {/* Solo del lado de la ferretería: el panel de plataforma no factura. */}
        {!isPublic && <AvisoTimbrado />}
        {!isPublic && <AvisoLegal />}
        <div className={`p-4 sm:p-6 lg:p-8 ${location.pathname.includes('/carrito') ? 'h-[calc(100vh-4rem)] flex flex-col' : ''}`}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}


/**
 * La barra lateral, agrupada por secciones.
 *
 * Es el mismo componente para la ferretería y para el panel de plataforma: la
 * barra tenía el mismo marcado escrito dos veces, con diferencias que no
 * respondían a nada —una mostraba avisos y la otra no, una envolvía la
 * etiqueta y la otra no— y cualquier mejora había que hacerla dos veces.
 *
 * Los títulos de sección no son botones: no llevan a ningún lado. Se marcan
 * con `role="presentation"` para que un lector de pantalla no los anuncie como
 * algo que se puede activar.
 */
function MenuLateral({ secciones, activo, onNavegar, avisos = {} }) {
  const clase = (esActivo, destacado) => {
    if (destacado) {
      // La acción principal se ve como un botón y no como un renglón más.
      return esActivo
        ? 'flex items-center gap-3 px-4 py-3 mx-3 my-1 rounded-xl font-bold transition-colors bg-brand-400 text-negro-950'
        : 'flex items-center gap-3 px-4 py-3 mx-3 my-1 rounded-xl font-bold transition-colors bg-brand-500 text-negro-950 hover:bg-brand-400';
    }
    return esActivo
      ? 'flex items-center gap-3 px-4 py-2.5 mx-3 my-0.5 rounded-xl font-bold transition-colors bg-brand-500 text-negro-950'
      : 'flex items-center gap-3 px-4 py-2.5 mx-3 my-0.5 rounded-xl font-medium transition-colors text-negro-300 hover:bg-negro-800 hover:text-white';
  };

  return (
    <>
      {secciones.map((seccion, i) => (
        <div key={seccion.titulo || `principal-${i}`} className={seccion.titulo ? 'mt-5' : ''}>
          {seccion.titulo && (
            <p
              role="presentation"
              className="px-7 mb-1.5 text-[10px] font-black uppercase tracking-widest text-negro-500"
            >
              {seccion.titulo}
            </p>
          )}
          {seccion.items.map(item => {
            const aviso = avisos[item.path];
            return (
              <button
                key={item.path}
                onClick={() => onNavegar(item.path)}
                aria-current={activo(item.path) ? 'page' : undefined}
                className={`w-full text-left ${clase(activo(item.path), item.destacado)}`}
              >
                {item.icon}
                <span className="flex-1">{item.label}</span>
                {aviso > 0 && (
                  <span className="ml-auto bg-error-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                    {aviso}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </>
  );
}
